import * as PropTypes from 'prop-types';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import * as modals from 'util/Modals';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import * as StageRunActions from 'workflows/StageRunActions';
import {STAGE_RUN_FULL_SHAPE, StageRun} from 'workflows/WorkflowConstants';

interface StageRunMenuProps {
  onEditStageRun: () => void;
  onOpenSetExperimentalCondition: () => void;
  openSetExperimentalConditionDisabled: boolean;
  onCopySelectedPath: () => void;
  stageRun: StageRun;
  isStageEditable: boolean;
  isWorkflowEditable: boolean;
}

class StageRunMenu extends React.Component<StageRunMenuProps> {
  static propTypes = {
    onEditStageRun: PropTypes.func.isRequired,
    onOpenSetExperimentalCondition: PropTypes.func.isRequired,
    openSetExperimentalConditionDisabled: PropTypes.bool.isRequired,
    onCopySelectedPath: PropTypes.func.isRequired,
    stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
    isStageEditable: PropTypes.bool.isRequired,
    isWorkflowEditable: PropTypes.bool.isRequired,
  };

  _updateStageRunStatus(status: STAGE_RUN_STATUS): void {
    StageRunActions.bulkUpdateStageRunStatus({
      updateStatusSpecifiers: [
        {
          stageRunId: this.props.stageRun.id,
          status,
        },
      ],
    });
  }

  _handleSelect(status: STAGE_RUN_STATUS): void {
    if (status === STAGE_RUN_STATUS.DISCARDED) {
      modals
        .confirm(
          'Confirm discard stage run',
          `Discard the ${this.props.stageRun.name} stage run and delete all downstream stage runs? \
           This action is not reversible.`,
          'danger',
          {acceptText: 'Discard', rejectText: 'Cancel'}
        )
        .done(() => {
          this._updateStageRunStatus(status);
        });
    } else {
      this._updateStageRunStatus(status);
    }
  }

  _handleClickDeleteStageRun = (): void => {
    StageRunActions.deleteStageRun(this.props.stageRun, {shouldDeleteDownstreamRuns: false});
  };

  _handleClickDeleteStageRunAndDownstreamRuns = (): void => {
    StageRunActions.deleteStageRun(this.props.stageRun, {shouldDeleteDownstreamRuns: true});
  };

  _handleRerunStageRun = (): void => {
    StageRunActions.rerunStageRun(this.props.stageRun.id);
  };

  _handleDuplicateBranch = (): void => {
    this.props.onCopySelectedPath();
    analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.DUPLICATED_BRANCH);
  };

  override render(): JSX.Element {
    const {
      onEditStageRun,
      onOpenSetExperimentalCondition,
      stageRun,
      isStageEditable,
      isWorkflowEditable,
      openSetExperimentalConditionDisabled,
    } = this.props;
    const isStageRunEditable = stageRun.stageEntry.permissions.writable;

    return (
      <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM}>
        {isStageRunEditable && (
          <Section>
            <Option>
              Update status
              <Menu>
                <Option
                  onSelect={() => {
                    this._handleSelect(STAGE_RUN_STATUS.INITIALIZED);
                  }}
                >
                  Initialized
                </Option>
                <Option
                  onSelect={() => {
                    this._handleSelect(STAGE_RUN_STATUS.COMPLETED);
                  }}
                >
                  Completed
                </Option>
                <Option
                  onSelect={() => {
                    this._handleSelect(STAGE_RUN_STATUS.DISCARDED);
                  }}
                >
                  Discarded
                </Option>
              </Menu>
            </Option>
          </Section>
        )}
        {(isStageRunEditable || isStageEditable || isWorkflowEditable) && (
          <Section>
            {isStageRunEditable && (
              <Option
                onSelect={() => {
                  onEditStageRun();
                }}
              >
                Edit run
              </Option>
            )}
            {isStageRunEditable && (
              <Option
                isDisabled={openSetExperimentalConditionDisabled}
                onSelect={() => {
                  onOpenSetExperimentalCondition();
                }}
              >
                Set experimental conditions
              </Option>
            )}
            {isStageEditable && (
              <Option
                onSelect={() => {
                  this._handleClickDeleteStageRun();
                }}
              >
                Delete run
              </Option>
            )}
            {isWorkflowEditable && (
              <Option
                onSelect={() => {
                  this._handleClickDeleteStageRunAndDownstreamRuns();
                }}
              >
                Delete this and all downstream runs
              </Option>
            )}
          </Section>
        )}
        {(isStageEditable || isWorkflowEditable) && (
          <Section>
            {isStageEditable && (
              <Option
                onSelect={() => {
                  this._handleRerunStageRun();
                }}
              >
                Repeat run
              </Option>
            )}
            {isWorkflowEditable && (
              <Option
                onSelect={() => {
                  this._handleDuplicateBranch();
                }}
              >
                Duplicate branch
              </Option>
            )}
          </Section>
        )}
      </Menu>
    );
  }
}

export default StageRunMenu;
